/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import cx from 'classnames';

import { unstable } from '@broadleaf/admin-components';
import PopoverLabel from '../Popover/PopoverLabel';
import { FormattedMessage } from 'react-intl';
import messages from './FulfillmentCardHeader.messages';

const { useFormatMessage, Currency } = unstable;

const AuroraFulfillmentCardHeader = ({ fulfillment }) => {
  return (
    // using negative margin on medium to offset the margin on headerDetails
    // which keep the bottom margin since they can wrap
    // the right padding is the offset for the show/hide button
    <div className="tw-flex tw-flex-1 tw-flex-col md:tw--mb-3 md:tw-flex-row md:tw-pr-12 xl:tw-mb-0">
      <div className="tw-flex tw-w-full tw-flex-col md:tw-w-4/5 md:tw-flex-row md:tw-flex-wrap">
        <HeaderDetail
          label={
            <FormattedMessage id="OrderView.FulfillmentCard.referenceNumber" />
          }
        >
          <span className="tw-text-sm tw-font-medium">
            {fulfillment.groupReferenceNumber}
          </span>
        </HeaderDetail>
        {fulfillment.address && (
          <HeaderDetail
            label={
              <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentAddressDetail" />
            }
          >
            <DeliverTo address={fulfillment.address} />
          </HeaderDetail>
        )}
        {fulfillment.fulfillmentOption &&
          fulfillment.fulfillmentOption.description && (
            <HeaderDetail
              label={
                <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentMethodDetail" />
              }
            >
              <span
                className="tw-truncate tw-text-sm tw-font-medium tw-text-gray-700"
                title={fulfillment.fulfillmentOption.description}
              >
                {fulfillment.fulfillmentOption.description}
              </span>
            </HeaderDetail>
          )}
        <HeaderDetail
          label={
            <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentTotalDetail" />
          }
        >
          <Total fulfillment={fulfillment} />
        </HeaderDetail>
      </div>
    </div>
  );
};

const DeliverTo = ({ address }) => {
  const displayName =
    address.fullName || `${address.firstName} ${address.lastName}`;
  return (
    <PopoverLabel label={displayName} position="bottom-left">
      <div className="tw-p-2 tw-font-semibold">{displayName}</div>
      <hr className="tw-border-gray-300" />
      <Address address={address} />
    </PopoverLabel>
  );
};

const Total = ({ fulfillment }) => {
  const formatMessage = useFormatMessage();
  const {
    fulfillmentTotal,
    merchandiseTotal,
    grandTotal,
    taxTotal,
    coreCostTotal
  } = fulfillment;
  return (
    <PopoverLabel
      label={
        <Currency currency={grandTotal.currency} value={grandTotal.amount} />
      }
      position="bottom-left"
    >
      <div className="tw-p-2">
        {merchandiseTotal && (
          <TotalDetail
            className="tw-mb-1 tw-text-sm"
            label={
              <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentMerchandiseTotal" />
            }
            total={merchandiseTotal}
          />
        )}
        {!!coreCostTotal && (
          <TotalDetail
            className="tw-mb-1 tw-text-sm"
            label={formatMessage(messages.fulfillmentCoreCostTotal)}
            total={coreCostTotal}
          />
        )}
        {fulfillmentTotal && (
          <TotalDetail
            className="tw-mb-1 tw-text-sm"
            label={
              <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentChargeTotal" />
            }
            total={fulfillmentTotal}
          />
        )}
        {taxTotal && (
          <TotalDetail
            className="tw-mb-1 tw-text-sm"
            label={
              <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentTaxTotal" />
            }
            total={taxTotal}
          />
        )}
      </div>
      <hr className="tw-border-gray-300" />
      <div className="tw-p-2">
        <TotalDetail
          className="tw-text-base"
          label={
            <FormattedMessage id="OrderView.FulfillmentCard.fulfillmentGrandTotal" />
          }
          total={grandTotal}
        />
      </div>
    </PopoverLabel>
  );
};

const TotalDetail = ({ className, label, total }) => {
  return (
    <div className={cx(className, 'tw-flex tw-justify-between')}>
      <div className="tw-text-gray-700">{label}</div>
      <div className="tw-font-medium tw-text-gray-900">
        <Currency currency={total.currency} value={total.amount} />
      </div>
    </div>
  );
};

const Address = ({ address }) => {
  return (
    <ul className="tw-list-none tw-p-2 tw-text-sm">
      <li>
        {`${address.addressLine1} ${address.addressLine2 || ''} ${
          address.addressLine3 || ''
        }`.trim()}
      </li>
      <li>
        {address.city}, {address.stateProvinceRegion} {address.postalCode}
      </li>
      <li>{address.country}</li>
      {address.phonePrimary && (
        <li>Phone: {address.phonePrimary.phoneNumber}</li>
      )}
    </ul>
  );
};

const HeaderDetail = ({ children, className, label }) => {
  return (
    <div
      className={cx(
        className,
        'tw-mb-3 tw-flex tw-flex-col md:tw-mr-5 md:tw-items-start md:tw-text-left xl:tw-mb-0'
      )}
    >
      <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-tight tw-text-gray-600">
        {label}
      </span>
      {children}
    </div>
  );
};

export default AuroraFulfillmentCardHeader;
